import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import { especialidadService, Especialidad } from '../services/especialidadService';
import { videoEstudioService } from '../services/videoEstudioService';
import { temaService, Tema } from '../services/temaService';
import { flashcardService } from '../services/flashcardService';
import { dashboardService } from '../services/dashboardService';
import { examenService } from '../services/examenService';
import { AmigosService } from '../services/amigosService';
import { profileService } from '../services/profileService';
import { UsuarioProfile } from '../types/profile';
import { notificationService } from '../services/notificationService';
import { userSubscriptionService } from '../services/userSubscriptionService';
import {
  userManagementService,
  UserFilters,
  UserManagement,
  UserStatistics,
  UserMetrics,
  PageResponse,
} from '../services/userManagementService';
import {
  paymentHistoryService,
  PaymentHistoryDTO,
  FinanceStats,
} from '../services/paymentHistoryService';
import { subscriptionPlanService, SubscriptionPlanDTO } from '../services/subscriptionPlanService';
import { dificultadService } from '../services/dificultadService';
import reactivosService, { ReactivosPage, ReactivosPageParams } from '../services/reactivosService';
import type { Dificultad } from '../types/reactivos.types';
import { VideoEstudio } from '../Models/VideoEstudio';
import { FlashcardEstadisticasDTO } from '../Models/Flashcard';

/**
 * Definiciones centralizadas de queries con el helper `queryOptions` de React Query v5.
 * Se reutilizan tanto en las páginas (useQuery) como en el prefetch al iniciar sesión
 * (queryClient.prefetchQuery). El tipo de retorno se anota explícitamente para que el
 * `data` quede tipado aunque algún service devuelva `any`.
 */

export const especialidadesQuery = () =>
  queryOptions({
    queryKey: ['especialidades'],
    queryFn: (): Promise<Especialidad[]> => especialidadService.getEspecialidades(),
  });

export const temasQuery = () =>
  queryOptions({
    queryKey: ['temas'],
    queryFn: (): Promise<Tema[]> => temaService.listar(),
  });

export const videosQuery = () =>
  queryOptions({
    queryKey: ['videos'],
    queryFn: (): Promise<VideoEstudio[]> => videoEstudioService.getAll(),
  });

export const flashcardStatsQuery = (usuarioId: number) =>
  queryOptions({
    queryKey: ['flashcardStats', usuarioId],
    queryFn: (): Promise<FlashcardEstadisticasDTO> => flashcardService.obtenerEstadisticas(usuarioId),
    enabled: usuarioId > 0,
  });

// Stats que cambian al terminar exámenes: muestran lo cacheado al instante pero
// revalidan al entrar (stale-while-revalidate), para reflejar la actividad reciente.
export const dashboardStatsQuery = () =>
  queryOptions({
    queryKey: ['dashboardStats'],
    queryFn: () => dashboardService.getEstudianteStats(),
    staleTime: 0,
  });

export const analyticsQuery = () =>
  queryOptions({
    queryKey: ['analytics'],
    queryFn: () => dashboardService.getAnalytics(),
    staleTime: 0,
  });

export const historialQuery = () =>
  queryOptions({
    queryKey: ['historial'],
    queryFn: () => dashboardService.getHistorial(),
    staleTime: 0,
  });

export const progresoEspecialidadQuery = () =>
  queryOptions({
    queryKey: ['progresoEspecialidad'],
    queryFn: () => dashboardService.getProgresoEspecialidad(),
    staleTime: 0,
  });

export const comparativaQuery = (especialidad?: string) =>
  queryOptions({
    queryKey: ['comparativa', especialidad ?? null],
    queryFn: () => dashboardService.getComparativa(especialidad),
    staleTime: 0,
  });

export const historialExamenesQuery = (usuarioId?: number) =>
  queryOptions({
    queryKey: ['historialExamenes', usuarioId],
    queryFn: () => examenService.obtenerHistorialExamenes(usuarioId as number),
    enabled: !!usuarioId,
    staleTime: 0,
  });

export const rankingQuery = (top = 50) =>
  queryOptions({
    queryKey: ['ranking', top],
    queryFn: () => dashboardService.getRanking(top),
  });

// Perfil (público/propio) por id de usuario. Se prefetchea el propio al iniciar
// sesión y lo consume PerfilPublicoPage. staleTime 0: revalida al entrar porque
// stats/amigos pueden cambiar, pero muestra lo cacheado al instante.
export const perfilQuery = (userId?: number) =>
  queryOptions({
    queryKey: ['perfil', userId],
    queryFn: (): Promise<UsuarioProfile> => profileService.getUserProfile(userId as number),
    enabled: !!userId,
    staleTime: 0,
  });

export const notificationsQuery = () =>
  queryOptions({
    queryKey: ['notifications'],
    queryFn: () => notificationService.getMyNotifications(0, 50),
    // Las notificaciones deben estar frescas: muestra lo cacheado al instante pero
    // revalida siempre al entrar (stale-while-revalidate).
    staleTime: 0,
  });

export const solicitudesRecibidasQuery = () =>
  queryOptions({
    queryKey: ['solicitudesRecibidas'],
    queryFn: () => AmigosService.obtenerSolicitudesRecibidas(),
  });

export const solicitudesEnviadasQuery = () =>
  queryOptions({
    queryKey: ['solicitudesEnviadas'],
    queryFn: () => AmigosService.obtenerSolicitudesEnviadas(),
  });

export const amigosQuery = () =>
  queryOptions({
    queryKey: ['amigos'],
    queryFn: () => AmigosService.obtenerAmigos(),
  });

export const subscriptionStatusQuery = () =>
  queryOptions({
    queryKey: ['subscriptionStatus'],
    queryFn: () => userSubscriptionService.getSubscriptionStatus(),
  });

// ============================================================
// ADMIN — datos compartidos y de páginas pesadas
// ============================================================

// Especialidades/Temas/Dificultades cambian muy rara vez: caché largo.
export const adminEspecialidadesQuery = () =>
  queryOptions({
    queryKey: ['adminEspecialidades'],
    queryFn: (): Promise<Especialidad[]> => especialidadService.listar(),
    staleTime: 60 * 60 * 1000, // 1h
  });

export const adminTemasQuery = () =>
  queryOptions({
    queryKey: ['adminTemas'],
    queryFn: (): Promise<Tema[]> => temaService.listar(),
    staleTime: 60 * 60 * 1000,
  });

export const adminDificultadesQuery = () =>
  queryOptions({
    queryKey: ['adminDificultades'],
    queryFn: (): Promise<Dificultad[]> => dificultadService.listar(),
    staleTime: 60 * 60 * 1000,
  });

// Banco de reactivos: paginado server-side con filtros. Se prefetchea la primera
// página al login admin; al cambiar de página/filtro se mantiene la data previa
// visible (keepPreviousData) para que no parpadee el skeleton.
export const adminReactivosPageQuery = (params: ReactivosPageParams) =>
  queryOptions({
    queryKey: ['adminReactivos', params],
    queryFn: (): Promise<ReactivosPage> => reactivosService.getPaginado(params),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000, // 1 min
  });

// Planes de suscripción cambian muy poco; pueden vivir cacheados varios minutos.
export const adminSubscriptionPlansQuery = () =>
  queryOptions({
    queryKey: ['adminSubscriptionPlans'],
    queryFn: (): Promise<SubscriptionPlanDTO[]> => subscriptionPlanService.getAllPlans(),
    staleTime: 10 * 60 * 1000, // 10min
  });

// Estadísticas globales de usuarios (admin/statistics + dashboard de admin).
export const adminUserStatisticsQuery = () =>
  queryOptions({
    queryKey: ['adminUserStatistics'],
    queryFn: (): Promise<UserStatistics> => userManagementService.getUserStatistics(),
    staleTime: 5 * 60 * 1000,
  });

// Listado paginado de usuarios admin; queryKey depende de los filtros.
export const adminUsersQuery = (filters: UserFilters) =>
  queryOptions({
    queryKey: ['adminUsers', filters],
    queryFn: (): Promise<PageResponse<UserManagement>> => userManagementService.getUsers(filters),
    // Mantén lo previo mientras llega lo nuevo para que la tabla no parpadee.
    placeholderData: (prev: PageResponse<UserManagement> | undefined) => prev,
  });

// Detalle de usuario admin; sólo se dispara cuando un id válido es pasado.
export const adminUserDetailsQuery = (id?: number) =>
  queryOptions({
    queryKey: ['adminUserDetails', id],
    queryFn: (): Promise<UserManagement> => userManagementService.getUserDetails(id as number),
    enabled: !!id,
  });

export const adminUserMetricsQuery = (id?: number) =>
  queryOptions({
    queryKey: ['adminUserMetrics', id],
    queryFn: (): Promise<UserMetrics> => userManagementService.getUserMetrics(id as number),
    enabled: !!id,
  });

// Finanzas: stats blob (rango fechas) + pagos del rango.
export const adminFinanceStatsQuery = (startISO?: string, endISO?: string) =>
  queryOptions({
    queryKey: ['adminFinanceStats', startISO, endISO],
    queryFn: (): Promise<FinanceStats> =>
      paymentHistoryService.getFinanceStats(startISO as string, endISO as string),
    enabled: !!startISO && !!endISO,
    staleTime: 60 * 1000, // 1min
  });

export const adminPaymentsBetweenQuery = (startISO?: string, endISO?: string) =>
  queryOptions({
    queryKey: ['adminPaymentsBetween', startISO, endISO],
    queryFn: (): Promise<PaymentHistoryDTO[]> =>
      paymentHistoryService.getAllBetween(startISO as string, endISO as string),
    enabled: !!startISO && !!endISO,
    staleTime: 60 * 1000,
  });

// Resumen de suscripciones (KPIs del panel admin de suscripciones).
export const adminSubscriptionSummaryQuery = () =>
  queryOptions({
    queryKey: ['adminSubscriptionSummary'],
    queryFn: () =>
      import('../services/subscriptionDashboardService').then((m) =>
        m.subscriptionDashboardService.getSummary()
      ),
    staleTime: 5 * 60 * 1000,
  });

// Cupones de descuento (panel admin).
export const adminDiscountCouponsQuery = () =>
  queryOptions({
    queryKey: ['adminDiscountCoupons'],
    queryFn: () =>
      import('../services/discountCouponService').then((m) => m.discountCouponService.getAll()),
    staleTime: 60 * 1000,
  });
